/**
 * MeshWorkerPool - Manages a pool of mesh workers with grid buffer recycling
 * 
 * Dispatches SurfaceNet + geometry expansion work to web workers.
 * Grid buffers (Uint32Array, GRID_SIZE³) are recycled via Transferable round-trips
 * to avoid per-chunk allocations.
 * 
 * Two queues: priority (build preview) and regular (chunk remesh).
 * Priority tasks are always drained first when a worker becomes free.
 * 
 * Batch dispatch supports atomic completion and proper cancellation
 * that removes queued tasks and discards in-flight results.
 */

import type { MeshWorkerResponse, MeshSlotData } from './meshWorker.js';
import { meshWorkerBudget } from './workerBudget.js';
import { perfStats } from '../debug/PerformanceStats.js';
import { GRID_SIZE } from '@worldify/shared';

// Grid total voxel count: GRID_SIZE³
const GRID_LENGTH = GRID_SIZE * GRID_SIZE * GRID_SIZE;

/**
 * Number of mesh-worker threads, scaled to the machine (was a hardcoded 2). Meshing — not
 * generation — is the streaming display bottleneck: a cave-riddled chunk's SurfaceNet costs ~30× a
 * solid-rock chunk, and every loaded chunk meshes, so with caves on there is far more mesh work than
 * gen work, and two workers serialised it. Scaled with the same shape as the terrain pool; the two
 * pools peak at different phases of a chunk's life (gen → mesh), so the nominal thread count can
 * exceed cores without a matching steady-state oversubscription. Capped at 6 (diminishing returns +
 * each worker holds a grid-buffer pool).
 */
export function meshWorkerCount(): number {
  return meshWorkerBudget(); // shares one thread budget with the terrain pool — see workerBudget.ts
}

interface Task {
  id: number;
  chunkKey: string;
  grid: Uint32Array;
  skipHighBoundary: [boolean, boolean, boolean];
  /** LOD level baked into the output's vertex scale (0 = full resolution). */
  lod: number;
}

/** Result delivered to caller */
export interface MeshResult {
  chunkKey: string;
  solid: MeshSlotData;
  transparent: MeshSlotData;
  liquid: MeshSlotData;
}

/** Callback for when a single mesh result is ready */
export type MeshResultCallback = (result: MeshResult) => void;

/**
 * Pool of mesh workers with grid buffer recycling.
 */
export class MeshWorkerPool {
  private workers: Worker[] = [];
  private busyWorkers = new Set<Worker>();
  private nextId = 0;

  /** Regular-priority tasks (chunk remesh) */
  private taskQueue: Task[] = [];

  /** High-priority tasks (build preview) — drained first */
  private priorityQueue: Task[] = [];

  /** Callbacks for pending results, keyed by request id */
  private pendingCallbacks = new Map<number, (resp: MeshWorkerResponse) => void>();

  /** Pool of reusable grid buffers (returned by workers after each job) */
  private gridPool: Uint32Array[] = [];

  /** Track which chunk keys are in-flight (prevent duplicate dispatch) */
  private inFlight = new Set<string>();

  /** Chunk keys currently owned by a preview batch (regular remesh should skip) */
  private _previewChunks = new Set<string>();

  constructor(poolSize: number = meshWorkerCount()) {
    for (let i = 0; i < poolSize; i++) {
      const worker = new Worker(
        new URL('./meshWorker.ts', import.meta.url),
        { type: 'module' }
      );
      worker.onmessage = (e: MessageEvent<MeshWorkerResponse>) => {
        // OFF-FRAME main-thread work (between rAF callbacks) — counted for the panel, see PerformanceStats.
        perfStats.begin('offFrame');
        perfStats.begin('offMesh');
        this.handleResponse(worker, e.data);
        perfStats.end('offMesh');
        perfStats.end('offFrame');
      };
      this.workers.push(worker);
    }

    // Pre-allocate grid buffers (one per worker + a few spare)
    for (let i = 0; i < poolSize + 2; i++) {
      this.gridPool.push(new Uint32Array(GRID_LENGTH));
    }
  }

  /**
   * Take a grid buffer from the pool (or allocate if empty).
   * Caller fills this, then passes to dispatch().
   */
  takeGrid(): Uint32Array {
    return this.gridPool.pop() ?? new Uint32Array(GRID_LENGTH);
  }

  /**
   * Return a grid buffer to the pool. Use this for grids taken via takeGrid() that are consumed
   * synchronously on the main thread (e.g. the light-only resample path) rather than transferred
   * to a worker (workers return their grid automatically via the response).
   */
  returnGrid(grid: Uint32Array): void {
    this.gridPool.push(grid);
  }

  /**
   * Check if a chunk key is currently being meshed by a worker.
   */
  isInFlight(chunkKey: string): boolean {
    return this.inFlight.has(chunkKey);
  }

  /**
   * Check if a chunk key is owned by the active preview batch.
   * VoxelWorld.processRemeshQueue should skip these.
   */
  isPreviewChunk(chunkKey: string): boolean {
    return this._previewChunks.has(chunkKey);
  }

  /**
   * Dispatch a single chunk for meshing (regular priority).
   * The grid buffer is transferred to the worker (zero-copy) and will be
   * recycled when the result returns.
   */
  dispatch(
    chunkKey: string,
    grid: Uint32Array,
    skipHighBoundary: [boolean, boolean, boolean],
    callback: MeshResultCallback,
    lod: number = 0,
  ): void {
    const id = this.nextId++;
    this.inFlight.add(chunkKey);

    this.pendingCallbacks.set(id, (resp) => {
      this.inFlight.delete(chunkKey);
      callback({
        chunkKey: resp.chunkKey,
        solid: resp.solid,
        transparent: resp.transparent,
        liquid: resp.liquid,
      });
    });

    const task: Task = { id, chunkKey, grid, skipHighBoundary, lod };
    const freeWorker = this.getFreeWorker();

    if (freeWorker) {
      this.postToWorker(freeWorker, task);
    } else {
      this.taskQueue.push(task);
    }
  }

  /**
   * Dispatch multiple chunks as a priority batch. Callback fires only when ALL complete.
   * Priority tasks are drained before regular tasks whenever a worker becomes free.
   * 
   * Returns a cancel function that:
   * - Removes queued tasks (recycles their grids)
   * - Discards results for tasks already on workers
   * - Clears preview chunk tracking
   */
  dispatchBatch(
    items: Array<{
      chunkKey: string;
      grid: Uint32Array;
      skipHighBoundary: [boolean, boolean, boolean];
    }>,
    callback: (results: MeshResult[]) => void,
  ): () => void {
    const results: MeshResult[] = [];
    let remaining = items.length;
    let cancelled = false;
    const batchIds = new Set<number>();

    if (remaining === 0) {
      callback([]);
      return () => {};
    }

    // Track preview chunk keys
    for (const item of items) {
      this._previewChunks.add(item.chunkKey);
    }

    const onSingle = (result: MeshResult) => {
      if (cancelled) return;
      results.push(result);
      remaining--;
      if (remaining === 0) {
        // Clear preview tracking on completion
        for (const item of items) {
          this._previewChunks.delete(item.chunkKey);
        }
        callback(results);
      }
    };

    // Dispatch each item at priority level
    for (const item of items) {
      const id = this.nextId++;
      this.inFlight.add(item.chunkKey);
      batchIds.add(id);

      this.pendingCallbacks.set(id, (resp) => {
        this.inFlight.delete(item.chunkKey);
        onSingle({
          chunkKey: resp.chunkKey,
          solid: resp.solid,
          transparent: resp.transparent,
          liquid: resp.liquid,
        });
      });

      const task: Task = { id, chunkKey: item.chunkKey, grid: item.grid, skipHighBoundary: item.skipHighBoundary, lod: 0 };
      const freeWorker = this.getFreeWorker();

      if (freeWorker) {
        this.postToWorker(freeWorker, task);
      } else {
        // Priority queue — drained before regular taskQueue
        this.priorityQueue.push(task);
      }
    }

    // Cancel function: properly clean up queued + in-flight tasks
    return () => {
      cancelled = true;

      // Clear preview tracking
      for (const item of items) {
        this._previewChunks.delete(item.chunkKey);
      }

      // Remove queued tasks, recycle grids
      this.priorityQueue = this.priorityQueue.filter(t => {
        if (batchIds.has(t.id)) {
          this.gridPool.push(t.grid);
          this.pendingCallbacks.delete(t.id);
          this.inFlight.delete(t.chunkKey);
          return false;
        }
        return true;
      });

      // For tasks already on workers: replace callbacks with no-ops that still clean up
      for (const id of batchIds) {
        if (this.pendingCallbacks.has(id)) {
          this.pendingCallbacks.set(id, (resp) => {
            // Just clean up inFlight tracking, discard result
            this.inFlight.delete(resp.chunkKey);
          });
        }
      }
    };
  }

  /**
   * Cancel any in-flight work for a specific chunk key.
   * The worker still finishes, but the callback is silently replaced with a no-op.
   */
  cancelChunk(chunkKey: string): void {
    this.inFlight.delete(chunkKey);
    // Remove from both queues if not yet dispatched
    const filterQueue = (queue: Task[]): Task[] =>
      queue.filter(t => {
        if (t.chunkKey === chunkKey) {
          this.gridPool.push(t.grid);
          this.pendingCallbacks.delete(t.id);
          return false;
        }
        return true;
      });
    this.taskQueue = filterQueue(this.taskQueue);
    this.priorityQueue = filterQueue(this.priorityQueue);
  }

  /** Handle a response from a worker */
  private handleResponse(worker: Worker, resp: MeshWorkerResponse): void {
    this.busyWorkers.delete(worker);

    // Recycle the grid buffer
    this.gridPool.push(resp.grid);

    // Invoke callback
    const cb = this.pendingCallbacks.get(resp.id);
    this.pendingCallbacks.delete(resp.id);
    if (cb) {
      cb(resp);
    }

    // Process next queued task (priority first)
    this.drainQueue();
  }

  /** Try to dispatch queued tasks to free workers. Priority queue drained first. */
  private drainQueue(): void {
    while (this.priorityQueue.length > 0 || this.taskQueue.length > 0) {
      const freeWorker = this.getFreeWorker();
      if (!freeWorker) break;
      // Priority first
      const task = this.priorityQueue.length > 0
        ? this.priorityQueue.shift()!
        : this.taskQueue.shift()!;
      this.postToWorker(freeWorker, task);
    }
  }

  /** Find a worker that isn't busy */
  private getFreeWorker(): Worker | undefined {
    return this.workers.find(w => !this.busyWorkers.has(w));
  }

  /** Post a task to a worker, transferring the grid buffer */
  private postToWorker(worker: Worker, task: Task): void {
    this.busyWorkers.add(worker);
    worker.postMessage(
      { id: task.id, chunkKey: task.chunkKey, grid: task.grid, skipHighBoundary: task.skipHighBoundary, lod: task.lod },
      [task.grid.buffer],
    );
  }

  /**
   * Cancel EVERY in-flight and queued task, keeping the workers alive — the bulk counterpart of
   * `cancelChunk`, for a world switch or a clear-and-reload.
   *
   * The pool deliberately survives those (spinning up mesh workers is expensive and the new world
   * needs them immediately), so without this its queues and `pendingCallbacks` carry straight over.
   * Chunk keys repeat in every world, so a result dispatched for the OLD world resolves against
   * whatever now holds that key and applies the previous world's geometry to it.
   *
   * Workers already mid-task still finish and reply; `handleResponse` finds no callback and drops
   * the result, recycling its grid — the same "the worker still finishes" contract `cancelChunk`
   * documents. Queued tasks have their grids returned to the pool rather than dropped, so the
   * buffers survive the switch.
   */
  cancelAll(): void {
    for (const t of this.taskQueue) this.gridPool.push(t.grid);
    for (const t of this.priorityQueue) this.gridPool.push(t.grid);
    this.taskQueue = [];
    this.priorityQueue = [];
    this.pendingCallbacks.clear();
    this.inFlight.clear();
    this._previewChunks.clear();
  }

  /** Terminate all workers */
  dispose(): void {
    for (const worker of this.workers) {
      worker.terminate();
    }
    this.workers = [];
    this.busyWorkers.clear();
    this.pendingCallbacks.clear();
    this.taskQueue = [];
    this.priorityQueue = [];
    this.gridPool = [];
    this.inFlight.clear();
    this._previewChunks.clear();
  }
}
